/**
 * @file useModalViewport.js
 * @description The one sizing and scroll engine for modal windows and sheets (2026-10-17).
 *   On iPhone, "vh" includes the area behind Safari's toolbars, so a window capped at 90vh had its header
 *   or footer cut off, and touch scrolling moved the page behind instead of the window. Every modal now
 *   sizes from the visible viewport (window.visualViewport) and locks the page while it is open.
 *
 *   useVisibleHeight()             visible viewport height in px (no browser toolbars, no keyboard)
 *   useModalViewport({ active })   locks the page while active; returns { isPhone, visibleH, maxH }
 *     maxH(fraction, minusPx, capPx)  e.g. maxH(0.9) for "90vh", maxH(1, 40) for "calc(100vh - 40px)",
 *                                     maxH(0.86, 0, 760) for "min(86vh, 760px)"
 *
 *   Scrolling panels should also set overscrollBehavior: "contain" (MODAL_SCROLL) so a fling at the end
 *   of the list does not scroll the page.
 * @module hooks/useModalViewport
 */
import { useEffect, useState } from "react";
import { useIsMobile } from "./useIsMobile";
import { useBodyScrollLock } from "./useBodyScrollLock";

const readVisible = () =>
  typeof window === "undefined" ? 0 : Math.round(window.visualViewport?.height || window.innerHeight || 0);

/** Visible viewport height in px; follows toolbar collapse, rotation and the on-screen keyboard. */
export function useVisibleHeight() {
  const [h, setH] = useState(readVisible);
  useEffect(() => {
    const vv = window.visualViewport;
    const onResize = () => setH(readVisible());
    window.addEventListener("resize", onResize);
    vv?.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      vv?.removeEventListener("resize", onResize);
    };
  }, []);
  return h;
}

/** Style for a scrolling panel inside a modal: momentum scroll, no scroll chaining to the page. */
export const MODAL_SCROLL = { overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" };

/**
 * @param {{ active?: boolean, phoneBreakpoint?: number }} [opts]
 *   active: lock the page and size while true (pass the open flag when the hook lives in a parent).
 */
export function useModalViewport({ active = true, phoneBreakpoint = 640 } = {}) {
  useBodyScrollLock(active);
  const visibleH = useVisibleHeight();
  const isPhone = useIsMobile(phoneBreakpoint);
  const maxH = (fraction = 0.92, minusPx = 0, capPx = 0) => {
    if (visibleH > 0) {
      const px = Math.max(160, Math.round(visibleH * fraction - minusPx));
      return capPx ? Math.min(capPx, px) : px;
    }
    const vh = minusPx ? `calc(${Math.round(fraction * 100)}vh - ${minusPx}px)` : `${Math.round(fraction * 100)}vh`;
    return capPx ? `min(${capPx}px, ${vh})` : vh;
  };
  return { isPhone, visibleH, maxH };
}
